import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import VideoSection, {
  type RowStyleAttributes,
} from 'hb-builder/components/BuilderComponents/common/Section/components/VideoSection';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';
import Grid, { Row } from 'hb-react/shared/components/DesignSystem/Grid';

import { wrapperInnerStyle } from '../style';

export interface CustomizableSectionProps {
  attributes: SectionItemAttributes;
  children: ReactNode;
  isMobile: boolean;
  isResizing: 'x' | 'y'; // TODO: use the types from BuilderContext when it's typed or the types from Resize when it's typed
  rowStyle: RowStyleAttributes;
}

const CustomizableSection = forwardRef(
  (
    {
      attributes,
      children,
      isMobile,
      isResizing,
      rowStyle,
    }: CustomizableSectionProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => (
    <Grid
      m="auto"
      colGap={0}
      displayGrid={isResizing === 'x'}
      mx={0}
      rowGap={0}
      width={rowStyle.maxWidth}
      height="100%"
    >
      {attributes.backgroundType === 'video' &&
      !!attributes.containerBackgroundVideo ? (
        <VideoSection
          attributes={attributes}
          children={children}
          isMobile={isMobile}
          rowStyle={rowStyle}
          ref={ref}
        />
      ) : (
        <StyledRow
          data-testid="customizable-section-classic"
          ref={ref}
          style={rowStyle}
          $isMobile={isMobile}
          $minHeight={attributes.height}
          $mobileDisplay={attributes.mobileDisplay}
          $themeOverridden={attributes.themeOverridden}
          $verticalAlign={attributes.verticalAlign}
        >
          {children}
        </StyledRow>
      )}
    </Grid>
  ),
);

export default CustomizableSection;

interface StyledRowProps {
  style: CustomizableSectionProps['rowStyle'];
  $isMobile: CustomizableSectionProps['isMobile'];
  $minHeight: SectionItemAttributes['height'];
  $mobileDisplay: SectionItemAttributes['mobileDisplay'];
  $themeOverridden: SectionItemAttributes['themeOverridden'];
  $verticalAlign: SectionItemAttributes['verticalAlign'];
}

const StyledRow = styled(Row)<StyledRowProps>`
  ${wrapperInnerStyle}
  ${({ $themeOverridden }) =>
    !$themeOverridden &&
    css`
      * {
        font-size: 100%;
      }
    `}
`;
